import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { chatThreadsApi, ChatUser } from "./chatThreads";
import type { DmMessageFile } from "./dmMessages";
import type { DownloadedUser } from "./projectMessages";

/**
 * Chat (DM) スレッド内メッセージの API クライアント。
 * ステータス・契約・システムメッセージは持たない、シンプルなメッセージの
 * やり取り。ファイル添付は upload API（purpose=chat_message）でアップロードし、
 * file_keys で紐付ける（オファー DM と同方式・同 payload 形式）。
 */

export interface ChatMessage {
  id: string;
  message: string;
  /** Chat は user メッセージのみ（MessageData 互換のため固定で "user"） */
  type: "user";
  author: ChatUser | null;
  parent_id: string | null;
  has_files: boolean;
  /** 添付ファイル（DM メッセージと同一 payload 形式） */
  files: DmMessageFile[];
  receivers: ChatUser[];
  created_at: string;
  updated_at: string;
}

export interface ChatMessagesData {
  messages: ChatMessage[];
  count: number;
  has_next: boolean;
  next_offset: number | null;
  limit: number;
  offset: number;
}

export interface ChatMessagesResponse {
  status: string;
  data: ChatMessagesData | null;
}

export interface GetChatMessagesParams {
  project_id: string;
  chat_id: string;
  limit?: number;
  offset?: number;
}

export interface SendChatMessageParams {
  project_id: string;
  chat_id: string;
  message: string;
  parent_id?: string | null;
  /** upload API（purpose=chat_message）で登録済みの file_key リスト */
  file_keys?: string[];
}

export interface SendChatMessageResponse {
  status: string;
  data: { message: ChatMessage };
}

export interface UpdateChatMessageParams {
  project_id: string;
  chat_id: string;
  message_id: string;
  message: string;
}

export interface UpdateChatMessageResponse {
  status: string;
  data: { message: ChatMessage };
}

export interface DeleteChatMessageParams {
  project_id: string;
  chat_id: string;
  message_id: string;
}

export interface MarkChatMessagesAsReadParams {
  project_id: string;
  chat_id: string;
  message_ids: string[];
  /**
   * 楽観更新用: キャッシュ上の自分の receivers.seen_at を即時更新する自分の
   * user id。未指定時は楽観更新せず API 送信のみ行う。
   */
  reader_user_id?: number;
}

export interface MarkChatMessagesAsReadResponse {
  status: string;
  data: { marked: number };
}

export interface MarkChatFilesAsDownloadedParams {
  project_id: string;
  chat_id: string;
  downloaded_file_ids: string[];
  /**
   * 楽観更新用: キャッシュ上の対象ファイルの `downloaded_users` へ即時追加する
   * 自分の情報。未指定時は楽観更新せず API 送信のみ行う。
   */
  downloaded_user?: DownloadedUser;
}

export interface MarkChatFilesAsDownloadedResponse {
  status: string;
  data: { downloaded_file_ids: string[] };
}

/**
 * chatThreadsApi の `getChatThreads` キャッシュを走査し、対象スレッドの
 * `unread_count` を `delta` だけ減算する（既読時のバッジ即時更新）。
 * dmMessages の同名ヘルパーと同じ実装方針。
 */
const decrementChatThreadUnreadCount = ({
  getState,
  dispatch,
  project_id,
  chat_id,
  delta,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  project_id: string;
  chat_id: string;
  delta: number;
}) => {
  const queries = getState()?.[chatThreadsApi.reducerPath]?.queries ?? {};
  type CachedQuery = {
    endpointName?: string;
    originalArgs?: { project_id?: string };
  };
  Object.values(queries).forEach((entry) => {
    const query = entry as CachedQuery | undefined;
    if (!query || query.endpointName !== "getChatThreads") return;
    const args = query.originalArgs;
    if (!args || args.project_id !== project_id) return;
    dispatch(
      chatThreadsApi.util.updateQueryData(
        "getChatThreads",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args as any,
        (draft) => {
          const thread = draft.data?.threads.find((t) => t.id === chat_id);
          if (!thread) return;
          thread.unread_count = Math.max(0, thread.unread_count - delta);
        },
      ),
    );
  });
};

/**
 * chatMessagesApi の `getChatMessages` キャッシュのうち、対象スレッド
 * （project_id + chat_id）のエントリすべてに `recipe` を適用し、
 * ロールバック用の patch 配列を返す。
 */
const patchCachedChatMessages = ({
  getState,
  dispatch,
  project_id,
  chat_id,
  recipe,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => any;
  project_id: string;
  chat_id: string;
  recipe: (draft: ChatMessagesResponse) => void;
}): { undo: () => void }[] => {
  const patches: { undo: () => void }[] = [];
  const queries = getState()?.[chatMessagesApi.reducerPath]?.queries ?? {};
  type CachedQuery = {
    endpointName?: string;
    originalArgs?: GetChatMessagesParams;
  };
  Object.values(queries).forEach((entry) => {
    const query = entry as CachedQuery | undefined;
    if (!query || query.endpointName !== "getChatMessages") return;
    const args = query.originalArgs;
    if (!args || args.project_id !== project_id || args.chat_id !== chat_id)
      return;
    const patch = dispatch(
      chatMessagesApi.util.updateQueryData(
        "getChatMessages",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args as any,
        recipe,
      ),
    );
    patches.push(patch);
  });
  return patches;
};

export const chatMessagesApi = createApi({
  reducerPath: "chatMessagesApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["ChatMessages"],
  endpoints: (builder) => ({
    getChatMessages: builder.query<ChatMessagesResponse, GetChatMessagesParams>({
      query: ({ project_id, chat_id, limit = 20, offset = 0 }) => {
        const params: Record<string, string> = {
          limit: limit.toString(),
          offset: offset.toString(),
        };
        const queryString = new URLSearchParams(params).toString();
        return `/api/me/${project_id}/chats/${chat_id}/messages?${queryString}`;
      },
      providesTags: ["ChatMessages"],
      keepUnusedDataFor: 30,
    }),

    // メッセージ送信。キャッシュ invalidate は行わず、呼び出し側で最新を取得・マージする。
    sendChatMessage: builder.mutation<
      SendChatMessageResponse,
      SendChatMessageParams
    >({
      query: ({ project_id, chat_id, message, parent_id, file_keys }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}/messages`,
        method: "POST",
        body: {
          message,
          ...(parent_id && { parent_id }),
          ...(file_keys && file_keys.length > 0 && { file_keys }),
        },
      }),
    }),

    // 現状 FE には編集 UI が無いため未使用。BE の PATCH /messages/<id> 対応として用意。
    updateChatMessage: builder.mutation<
      UpdateChatMessageResponse,
      UpdateChatMessageParams
    >({
      query: ({ project_id, chat_id, message_id, message }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}/messages/${message_id}`,
        method: "PATCH",
        body: { message },
      }),
    }),

    // 現状 FE には削除 UI が無いため未使用。BE の DELETE /messages/<id> 対応として用意。
    deleteChatMessage: builder.mutation<
      { status: string },
      DeleteChatMessageParams
    >({
      query: ({ project_id, chat_id, message_id }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}/messages/${message_id}`,
        method: "DELETE",
      }),
    }),

    // 複数メッセージの既読登録。成功時に chatThreadsApi の unread_count を楽観減算する。
    markChatMessagesAsRead: builder.mutation<
      MarkChatMessagesAsReadResponse,
      MarkChatMessagesAsReadParams
    >({
      query: ({ project_id, chat_id, message_ids }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}/messages`,
        method: "PATCH",
        body: { message_ids },
      }),
      async onQueryStarted(
        { project_id, chat_id, message_ids, reader_user_id },
        { dispatch, queryFulfilled, getState },
      ) {
        // 楽観更新: 自分の receiver の seen_at を即時に立てる（WS の
        // message.chat.read を待たずに既読が確定し、既読化 effect の同一 ID
        // 再送も抑止できる）。失敗時もロールバックしない: 戻すと呼び出し側の
        // 既読化 effect が同じ ID を再送し続けるループになり得るため、
        // 実状態は次回 fetch / WS で整合させる。
        if (reader_user_id != null && message_ids.length > 0) {
          const idSet = new Set(message_ids);
          const seenAt = new Date().toISOString();
          patchCachedChatMessages({
            getState,
            dispatch,
            project_id,
            chat_id,
            recipe: (draft) => {
              draft.data?.messages.forEach((message) => {
                if (!idSet.has(message.id)) return;
                const mine = message.receivers.find(
                  (r) => r.id === reader_user_id,
                );
                if (mine && !mine.seen_at) {
                  mine.seen_at = seenAt;
                }
              });
            },
          });
        }
        try {
          // BE が実際に既読化した件数（marked）で減算する。存在しない / 既読済みの
          // ID を送ってもリクエスト件数で過減算しないよう、レスポンス値を用いる。
          const { data } = await queryFulfilled;
          decrementChatThreadUnreadCount({
            getState,
            dispatch,
            project_id,
            chat_id,
            delta: data?.data?.marked ?? 0,
          });
        } catch {
          // 既読 API 失敗時は unread_count を変更しない（次回 fetch で整合）
        }
      },
    }),

    // ファイルのダウンロード既読登録（オファー DM の markDmFilesAsDownloaded と同仕様）。
    // downloaded_user 指定時は getChatMessages キャッシュの downloaded_users へ
    // 楽観追加し、API 失敗時はロールバックする。
    markChatFilesAsDownloaded: builder.mutation<
      MarkChatFilesAsDownloadedResponse,
      MarkChatFilesAsDownloadedParams
    >({
      query: ({ project_id, chat_id, downloaded_file_ids }) => ({
        url: `/api/me/${project_id}/chats/${chat_id}/messages`,
        method: "PATCH",
        body: { downloaded_file_ids },
      }),
      async onQueryStarted(
        { project_id, chat_id, downloaded_file_ids, downloaded_user },
        { dispatch, queryFulfilled, getState },
      ) {
        let patches: { undo: () => void }[] = [];
        if (downloaded_user) {
          const fileIdSet = new Set(downloaded_file_ids);
          patches = patchCachedChatMessages({
            getState,
            dispatch,
            project_id,
            chat_id,
            recipe: (draft) => {
              draft.data?.messages.forEach((message) => {
                message.files?.forEach((file) => {
                  if (!fileIdSet.has(file.id)) return;
                  const users = file.downloaded_users ?? [];
                  if (users.some((u) => u.id === downloaded_user.id)) return;
                  file.downloaded_users = [...users, { ...downloaded_user }];
                });
              });
            },
          });
        }
        try {
          await queryFulfilled;
        } catch {
          // DL 既読 API 失敗時は楽観更新をロールバック（次回 fetch で整合）
          patches.forEach((p) => p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
  useUpdateChatMessageMutation,
  useDeleteChatMessageMutation,
  useMarkChatMessagesAsReadMutation,
  useMarkChatFilesAsDownloadedMutation,
} = chatMessagesApi;
